// 消息内容缓存工具

import { ChatCacheData } from '../types';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  accessCount: number;
}

// 聊天会话缓存类
export class ChatCache {
  private static readonly CACHE_KEY_PREFIX = 'deepbi_chat_';

  // 生成基于邮箱的缓存 key
  private static generateCacheKey(email: string): string {
    return `${this.CACHE_KEY_PREFIX}${email}`;
  }

  static save(data: ChatCacheData, email?: string): void {
    try {
      // 如果没有提供邮箱，使用数据中的邮箱
      const cacheKey = email ? this.generateCacheKey(email) : this.generateCacheKey(data.email);
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save chat cache:', error);
    }
  }

  static load(email?: string): ChatCacheData | null {
    try {
      if (!email) {
        // 如果没有提供邮箱，尝试查找所有相关的缓存
        const keys = Object.keys(localStorage).filter(key => key.startsWith(this.CACHE_KEY_PREFIX));
        if (keys.length > 0) {
          const cached = localStorage.getItem(keys[0]);
          return cached ? JSON.parse(cached) : null;
        }
        return null;
      }
      
      const cacheKey = this.generateCacheKey(email);
      const cached = localStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn('Failed to load chat cache:', error);
      return null;
    }
  }

  static clear(email?: string): void {
    try {
      if (!email) {
        // 如果没有提供邮箱，清除所有相关缓存
        const keys = Object.keys(localStorage).filter(key => key.startsWith(this.CACHE_KEY_PREFIX));
        keys.forEach(key => localStorage.removeItem(key));
      } else {
        const cacheKey = this.generateCacheKey(email);
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      console.warn('Failed to clear chat cache:', error);
    }
  }

  // 获取所有缓存的邮箱列表
  static getAllCachedEmails(): string[] {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(this.CACHE_KEY_PREFIX));
      return keys.map(key => key.replace(this.CACHE_KEY_PREFIX, ''));
    } catch (error) {
      console.warn('Failed to get cached emails:', error);
      return [];
    }
  }
}

export class MessageContentCache {
  private cache = new Map<string, CacheItem<any>>();
  private maxSize = 1000; // 最大缓存条目数
  private maxAge = 5 * 60 * 1000; // 5分钟过期时间
  private accessThreshold = 3; // 访问次数阈值

  // 生成缓存键
  private generateKey(content: string, type: string): string {
    return `${type}_${content.length}_${this.hashString(content)}`;
  }

  // 简单字符串哈希
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    return hash.toString(36);
  }

  // 设置缓存
  set<T>(key: string, data: T): void {
    // 如果缓存已满，清理最少使用的条目
    if (this.cache.size >= this.maxSize) {
      this.cleanup();
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      accessCount: 1
    });
  }

  // 获取缓存
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // 检查是否过期
    if (Date.now() - item.timestamp > this.maxAge) {
      this.cache.delete(key);
      return null;
    }

    // 增加访问次数
    item.accessCount++;
    return item.data;
  }

  // 清理过期和最少使用的缓存
  private cleanup(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    // 按访问次数和时间排序，删除最少使用的
    entries.sort((a, b) => {
      const aScore = a[1].accessCount - (now - a[1].timestamp) / 1000;
      const bScore = b[1].accessCount - (now - b[1].timestamp) / 1000;
      return aScore - bScore;
    });

    // 删除前20%的条目
    const deleteCount = Math.floor(entries.length * 0.2);
    for (let i = 0; i < deleteCount; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  // 清理所有缓存
  clear(): void {
    this.cache.clear();
  }

  // 获取缓存统计信息
  getStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: 0 // 可以添加命中率统计
    };
  }
}

// 全局缓存实例
export const messageContentCache = new MessageContentCache();

// 缓存装饰器
export function cached<T extends (...args: any[]) => any>(
  fn: T,
  keyGenerator?: (...args: Parameters<T>) => string
): T {
  return ((...args: Parameters<T>) => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
    const cached = messageContentCache.get<ReturnType<T>>(key);
    
    if (cached !== null) {
      return cached;
    }

    const result = fn(...args);
    messageContentCache.set(key, result);
    return result;
  }) as T;
}